import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { CreatePipelineInput } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { usePipeline, useCreatePipeline, useUpdatePipeline } from '../../api/pipelines';

interface StageRow {
  name: string;
  isClosedWon: boolean;
  isClosedLost: boolean;
}

const BLANK_STAGE: StageRow = { name: '', isClosedWon: false, isClosedLost: false };

/**
 * Mirrors the backend's `createPipelineBody`/`updatePipelineBody` limits
 * (pipelines.schemas.ts). Array-item keys follow the same shape zod/Fastify
 * uses for `instancePath` (e.g. `/stages/0/name`), stripped of the leading
 * slash, so `fieldErrorsFromApiError`'s server-side keys line up with these
 * client-side ones.
 */
function validate(name: string, description: string, stages: StageRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const trimmedName = name.trim();
  if (trimmedName.length < 1) {
    errors.name = 'Enter a pipeline name.';
  } else if (trimmedName.length > 120) {
    errors.name = 'Name must be 120 characters or fewer.';
  }
  if (description.trim().length > 500) {
    errors.description = 'Description must be 500 characters or fewer.';
  }
  const nonBlank = stages.filter((s) => s.name.trim() !== '');
  if (nonBlank.length === 0) {
    errors.stages = 'Add at least one stage.';
  } else if (nonBlank.length > 30) {
    errors.stages = 'A pipeline may have at most 30 stages.';
  }
  stages.forEach((s, i) => {
    const trimmed = s.name.trim();
    if (trimmed === '') return; // blank rows are dropped on submit, not an error
    if (trimmed.length > 120) {
      errors[`stages/${i}/name`] = 'Stage name must be 120 characters or fewer.';
    }
    if (s.isClosedWon && s.isClosedLost) {
      errors[`stages/${i}/name`] = 'A stage cannot be both closed-won and closed-lost.';
    }
  });
  return errors;
}

export function PipelineFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const existing = usePipeline(id);
  const create = useCreatePipeline();
  const update = useUpdatePipeline(id ?? '');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [stages, setStages] = useState<StageRow[]>([
    { name: 'New', isClosedWon: false, isClosedLost: false },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && existing.data) {
      const p = existing.data;
      setName(p.name);
      setDescription(p.description ?? '');
      setIsDefault(p.isDefault);
      setStages(
        p.stages.map((s) => ({
          name: s.name,
          isClosedWon: s.isClosedWon,
          isClosedLost: s.isClosedLost,
        })),
      );
    }
  }, [isEdit, existing.data]);

  const busy = create.isPending || update.isPending;
  const patchStage = (i: number, patch: Partial<StageRow>) =>
    setStages((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  function clearFieldError(key: string) {
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(name, description, stages);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const cleaned = stages.map((s) => ({ ...s, name: s.name.trim() })).filter((s) => s.name !== '');
    const payload: CreatePipelineInput = {
      name: name.trim(),
      description: description.trim() || undefined,
      isDefault,
      stages: cleaned,
    };
    try {
      const saved =
        isEdit && id ? await update.mutateAsync(payload) : await create.mutateAsync(payload);
      toast.success(isEdit ? 'Pipeline updated.' : 'Pipeline created.');
      navigate('/pipelines', { replace: true });
      void saved;
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the pipeline.';
        setError(message);
        toast.error(message);
      }
    }
  }

  if (isEdit && existing.isPending) return <PageState kind="loading" message="Loading pipeline…" />;
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 640 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit pipeline' : 'New pipeline'}
      </h1>
      <Card>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearFieldError('description');
            }}
            error={fieldErrors.description}
          />
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            Default pipeline
          </label>

          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <span className="ne-field__label">Stages</span>
            {fieldErrors.stages ? (
              <span className="ne-field__error" role="alert">
                {fieldErrors.stages}
              </span>
            ) : null}
            {stages.map((s, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 160 }}>
                  <input
                    className={[
                      'ne-input',
                      fieldErrors[`stages/${i}/name`] ? 'ne-input--error' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={{ width: '100%' }}
                    placeholder={`Stage ${i + 1}`}
                    value={s.name}
                    aria-invalid={fieldErrors[`stages/${i}/name`] ? true : undefined}
                    onChange={(e) => {
                      patchStage(i, { name: e.target.value });
                      clearFieldError(`stages/${i}/name`);
                    }}
                  />
                  {fieldErrors[`stages/${i}/name`] ? (
                    <span className="ne-field__error" role="alert">
                      {fieldErrors[`stages/${i}/name`]}
                    </span>
                  ) : null}
                </div>
                <label
                  style={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={s.isClosedWon}
                    onChange={(e) => {
                      patchStage(i, { isClosedWon: e.target.checked, isClosedLost: false });
                      clearFieldError(`stages/${i}/name`);
                    }}
                  />
                  won
                </label>
                <label
                  style={{
                    display: 'flex',
                    gap: 4,
                    alignItems: 'center',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={s.isClosedLost}
                    onChange={(e) => {
                      patchStage(i, { isClosedLost: e.target.checked, isClosedWon: false });
                      clearFieldError(`stages/${i}/name`);
                    }}
                  />
                  lost
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStages((rows) => rows.filter((_, idx) => idx !== i))}
                  disabled={stages.length <= 1}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              onClick={() => setStages((rows) => [...rows, { ...BLANK_STAGE }])}
            >
              Add stage
            </Button>
          </div>

          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create pipeline'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/pipelines')}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/pipelines">← All pipelines</Link>
      </p>
    </div>
  );
}
